export type EditorState =
  | {
      status: "loading";
      value: "";
      savedValue: "";
    }
  | {
      status: "editing";
      value: string;
      savedValue: string;
    }
  | {
      status: "saving";
      value: string;
      savedValue: string;
      requestId: number;
    }
  | {
      status: "error";
      value: string;
      savedValue: string;
      message: string;
    };

export type EditorEvent =
  | { type: "load"; value: string | null }
  | { type: "sync"; value: string | null }
  | { type: "change"; value: string }
  | { type: "reset" }
  | { type: "saveStarted"; requestId: number }
  | { type: "saveSucceeded"; requestId: number; value: string }
  | { type: "saveFailed"; requestId: number; message: string };

export function createEditorState(value: string | null): EditorState {
  if (value === null) {
    return { status: "loading", value: "", savedValue: "" };
  }
  return { status: "editing", value, savedValue: value };
}

// 편집기에서 허용하는 전이를 한 곳에 둔다. saving 중 change와 현재 요청이 아닌 완료 이벤트는
// 무시한다. 그래서 화면마다 비동기 저장 경합을 따로 처리하지 않아도 된다.
export function editorReducer(state: EditorState, event: EditorEvent): EditorState {
  switch (event.type) {
    case "load":
      return createEditorState(event.value);
    case "sync":
      return syncExternalValue(state, event.value);
    case "change":
      if (state.status === "loading" || state.status === "saving") return state;
      return { status: "editing", value: event.value, savedValue: state.savedValue };
    case "reset":
      if (state.status === "loading" || state.status === "saving") return state;
      return { status: "editing", value: state.savedValue, savedValue: state.savedValue };
    case "saveStarted":
      if (state.status === "loading" || state.status === "saving") return state;
      return { ...state, status: "saving", requestId: event.requestId };
    case "saveSucceeded":
      if (state.status !== "saving" || state.requestId !== event.requestId) return state;
      return { status: "editing", value: event.value, savedValue: event.value };
    case "saveFailed":
      if (state.status !== "saving" || state.requestId !== event.requestId) return state;
      return {
        status: "error",
        value: state.value,
        savedValue: state.savedValue,
        message: event.message,
      };
  }
}

function syncExternalValue(state: EditorState, value: string | null): EditorState {
  if (state.status === "saving") return state;
  if (value === null) return createEditorState(null);
  if (state.status === "loading") return createEditorState(value);

  const nextValue = state.value === state.savedValue ? value : state.value;
  if (nextValue === state.value && value === state.savedValue) return state;

  return state.status === "error" && nextValue === state.value
    ? { ...state, savedValue: value }
    : { status: "editing", value: nextValue, savedValue: value };
}
